// src/components/open-houses/checkin/QuickCheckinModal.jsx
// Start the check-in kiosk WITHOUT a scheduled open house: the agent types
// the property address, we create the open house record on the spot, and the
// kiosk launches. Counters, Sort & Send, and feedback all work off that
// record exactly like a scheduled one.
import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useStateValue } from "../../../context/StateProvider";
import { adHocOpenHouseFields } from "../../../utils/checkinFlow";
import { createAdHocOpenHouse } from "../../../utils/firebasefunctions";

export default function QuickCheckinModal({ open, onClose }) {
  const [{ user }] = useStateValue();
  const navigate = useNavigate();
  const [address, setAddress] = useState("");
  const [starting, setStarting] = useState(false);

  if (!open) return null;

  const start = async () => {
    if (!address.trim()) {
      toast.error("Enter the property address.");
      return;
    }
    setStarting(true);
    const r = await createAdHocOpenHouse(adHocOpenHouseFields(address, user), user?.id);
    setStarting(false);
    if (r.success) {
      navigate(`/dashboard/oh-checkin/${r.id}`);
    } else {
      toast.error("Could not start. Check your connection and try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Quick Start Open House</h2>
            <p className="text-xs text-slate-500">
              No scheduled open house needed. Type the address and go.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>
        <div className="p-6">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
            Property address
          </label>
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") start(); }}
            placeholder="123 Maple Lane, Springfield"
            autoFocus
            className="w-full border border-slate-300 rounded-lg p-3 outline-none focus:border-slate-800 mb-2"
          />
          <p className="text-[11px] text-slate-400 mb-5">
            Today's open house record is created for you, assigned to you as the host. Sign-ins,
            counts, and lead sorting work just like a scheduled one.
          </p>
          <button
            onClick={start}
            disabled={starting}
            className="w-full bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white font-semibold rounded-lg py-3"
          >
            {starting ? "Starting..." : "Start Open House"}
          </button>
        </div>
      </div>
    </div>
  );
}
